"use client"

import React, { createContext, useContext, useState, useEffect } from 'react'
import { auth } from '../lib/firebase'
import { 
  browserLocalPersistence, 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  verifyBeforeUpdateEmail,
} from 'firebase/auth'
import { SubscriptionService } from '../services/subscription-service'
import { trackLogin, trackSignUp, trackAccountDeletion } from '../lib/analytics'
import { indexedDBService } from '../services/indexed-db-service'

const AuthContext = createContext()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [dataLoading, setDataLoading] = useState(false)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    auth.setPersistence(browserLocalPersistence).catch((err) => {
      console.warn('Auth persistence failed:', err)
    })

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user)
      setAuthLoading(false)
      setInitialized(true)
    })

    return unsubscribe
  }, [])

  const checkUserData = async () => {
    if (!user) return
    setDataLoading(true)
    try {
      await SubscriptionService.checkAndResetMonthlyDebates()
    } catch (error) {
      console.error('Error checking monthly reset:', error)
    } finally {
      setDataLoading(false)
    }
  }

  // Add force token refresh function
  const refreshUserToken = async () => {
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true);
    }
  };

  const value = {
    user,
    loading: !initialized || authLoading,
    dataLoading,
    login: async (email, password) => {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      if (!userCredential.user.emailVerified) {
        throw new Error('Please verify your email before logging in')
      }
      trackLogin('email')
      await checkUserData()
    },
    signup: async (email, password) => {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      await sendEmailVerification(userCredential.user)
      trackSignUp('email')
      return userCredential.user
    },
    logout: async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        // Clear the user's cached debates before logging out
        await indexedDBService.clearCache(currentUser.uid);
      }
      await signOut(auth)
    },
    deleteAccount: async () => {
      if (auth.currentUser) {
        trackAccountDeletion()
        await auth.currentUser.delete()
      }
    },
    sendVerificationEmail: async () => {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser)
      }
    },
    resetPassword: async (email) => {
      await sendPasswordResetEmail(auth, email)
    },
    isEmailVerified: () => {
      return auth.currentUser?.emailVerified ?? false
    },
    updateEmail: async (newEmail) => {
      if (auth.currentUser) {
        await verifyBeforeUpdateEmail(auth.currentUser, newEmail);
        return true;
      }
      throw new Error('No user is currently signed in');
    },
    refreshUserToken, // Expose the refresh function
  }

  if (!initialized || authLoading || dataLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    )
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  return useContext(AuthContext)
}

